import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTranslation } from 'react-i18next';
import { formatDateTR, formatDateTime } from '../../../utils/dateUtils';
import { formatCurrency } from '../../../constants/tableConfigurations';

const STATUS_CONFIG = {
  APPROVED: { color: 'success', icon: <CheckCircleOutlinedIcon fontSize="small" />, labelKey: 'expenses.status.approved' },
  REJECTED: { color: 'error', icon: <CancelOutlinedIcon fontSize="small" />, labelKey: 'expenses.status.rejected' },
  PENDING: { color: 'warning', icon: null, labelKey: 'expenses.status.pending' },
};

const InfoRow = ({ icon, label, value, valueColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Box sx={{ color: 'text.disabled', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} color={valueColor || 'text.primary'}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const ExpenseDetailModal = ({ open, onClose, expense }) => {
  const { t } = useTranslation();

  if (!expense) return null;

  const statusCfg = STATUS_CONFIG[expense.status] || STATUS_CONFIG.PENDING;

  const userName = expense.user
    ? `${expense.user.first_name || ''} ${expense.user.last_name || ''}`.trim()
    : '—';
  const managerName = expense.approvingManager
    ? `${expense.approvingManager.first_name || ''} ${expense.approvingManager.last_name || ''}`.trim()
    : expense.approving_manager
    ? `${expense.approving_manager.first_name || ''} ${expense.approving_manager.last_name || ''}`.trim()
    : null;

  const initials = userName !== '—'
    ? userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontSize: 16, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {userName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {expense.expenseTypeName || expense.expense_type?.expense_type_name || '—'}
            </Typography>
          </Box>
          <Chip
            onClick={ () => {}}
            icon={statusCfg.icon}
            label={t(statusCfg.labelKey)}
            color={statusCfg.color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Amount hero */}
        <Box sx={{ mt: 2.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} textTransform="uppercase">
            {t('expenses.table.amount', 'Tutar')}
          </Typography>
          <Typography variant="h4" fontWeight={800} color="primary.main" mt={0.25}>
            {formatCurrency(expense.amount)}
          </Typography>
          {expense.status === 'APPROVED' && (expense.paidAmount != null || expense.paid_amount != null) && (
            <Typography variant="caption" color="success.light" fontWeight={600}>
              {t('expenses.table.paidAmount', 'Ödenen')}: {formatCurrency(expense.paidAmount ?? expense.paid_amount)}
            </Typography>
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InfoRow
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label={t('expenses.table.expenseDate', 'Masraf Tarihi')}
            value={formatDateTR(expense.expenseDate || expense.expense_date)}
          />
          <InfoRow
            icon={<CategoryOutlinedIcon fontSize="small" />}
            label={t('expenses.table.expenseType', 'Masraf Türü')}
            value={expense.expenseTypeName || expense.expense_type?.expense_type_name}
          />
          <InfoRow
            icon={<NotesOutlinedIcon fontSize="small" />}
            label={t('expenses.table.description', 'Açıklama')}
            value={expense.description}
          />

          {expense.status !== 'PENDING' && managerName && (
            <>
              <Divider />
              <InfoRow
                icon={<PersonOutlineIcon fontSize="small" />}
                label={t('expenses.table.approvingManager', 'İşlemi Yapan')}
                value={managerName}
              />
              {(expense.approvingDate || expense.approving_date) && (
                <InfoRow
                  icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                  label={t('expenses.table.approvingDate', 'İşlem Tarihi')}
                  value={formatDateTime(expense.approvingDate || expense.approving_date)}
                />
              )}
            </>
          )}

          {expense.status === 'REJECTED' && (expense.rejectReason || expense.reject_reason) && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => `${theme.palette.error.main}18`,
                border: '1px solid',
                borderColor: 'error.main',
                borderOpacity: 0.3,
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
              }}
            >
              <CancelOutlinedIcon fontSize="small" sx={{ color: 'error.main', mt: 0.15, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="error.main" fontWeight={700} display="block" mb={0.5}>
                  {t('expenses.table.rejectReason', 'Red Sebebi')}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {expense.rejectReason || expense.reject_reason}
                </Typography>
              </Box>
            </Box>
          )}

          {expense.status === 'APPROVED' && (expense.paidAmount != null || expense.paid_amount != null) && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => `${theme.palette.success.main}18`,
                border: '1px solid',
                borderColor: 'success.main',
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <PaidOutlinedIcon fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="success.main" fontWeight={700} display="block">
                  {t('expenses.table.paidAmount', 'Ödenen Tutar')}
                </Typography>
                <Typography variant="body2" color="text.primary" fontWeight={600}>
                  {formatCurrency(expense.paidAmount ?? expense.paid_amount)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" fullWidth sx={{ borderRadius: 2 }}>
          {t('common.close', 'Kapat')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseDetailModal;
